import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap';
import { ActiveColumnsList } from '../dashboard/ActiveColumnsList';
import { Switch } from '../buttons/Swtich';
import { FaIcon } from '../FaIcon';
import { FieldsColumnsSettingsService } from '../../utils/FieldsColumnsSettingsService';
import { getFieldDescriptionKey, hasFieldDescription } from '../../utils/fieldDescriptions';
import { FieldHelpIcon } from '../elements/FieldHelpIcon';
import {
    renderViewSelector,
    renderActionButtons,
    renderSearchBar,
    renderError
} from '../shared/ColumnsSelectorModalHelpers';
import {
    separateFieldsByType,
    combineFields,
    getFieldType,
    filterFieldsByType
} from './DataExplorerFieldsHelper';

/**
 * Configuración de grupos que deben aparecer colapsados por defecto.
 * 
 * Para agregar un grupo que debe estar colapsado por defecto:
 * - Agregar el ID exacto del grupo (la clave en FIELDS_GROUP_DATA) a este array
 * 
 * Ejemplo:
 * - Para colapsar GA4: agregar el ID del grupo GA4 (ej: 'GA4' o 'ga4')
 * - Para colapsar múltiples grupos: ['GA4', 'META', 'otro_grupo_id']
 * 
 * Nota: Los IDs son las claves del objeto FIELDS_GROUP_DATA, no los nombres mostrados.
 */
const DEFAULT_COLLAPSED_GROUPS = ['GA4', 'META'];

class DataExplorerColumnsModalComponent extends Component {
    constructor(props) {
        super(props);
        // Convertir dimensions/metrics separados a array unificado de fields
        const activeFields = this.buildActiveFieldsFromProps(props.dimensions, props.metrics);
        
        // Verificar si las columnas actuales coinciden con el editingFieldsSet
        let activeView = null;
        let activeViewName = null;
        let saveMode = 'create';
        
        // Nota: No podemos llamar a fieldsMatchSet aquí porque aún no está definido
        // Se inicializará correctamente en componentDidUpdate cuando se abra el modal
        if (props.editingFieldsSet) {
            activeView = props.editingFieldsSet.id;
            activeViewName = props.editingFieldsSet.name;
            saveMode = 'update';
        }
        
        this.state = {
            searchQuery: "",
            activeFields: activeFields, // Array unificado de field IDs
            activeView: activeView,
            activeViewName: activeViewName,
            hasUnsavedChanges: false,
            originalFields: [...activeFields], // Array unificado para comparaciones
            collapsedSections: {},
            showSaveAsInput: false,
            newSetName: "",
            isSaving: false,
            savingError: null,
            saveMode: saveMode,
        };
        this.handleFieldChange = this.handleFieldChange.bind(this);
        this.handleFieldsOrderChange = this.handleFieldsOrderChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleColumnRemove = this.handleColumnRemove.bind(this);
        this.handleSaveFieldsSet = this.handleSaveFieldsSet.bind(this);
        this.handleSaveAsNew = this.handleSaveAsNew.bind(this);
        this.handleCancelSave = this.handleCancelSave.bind(this);
        this.handleUpdateView = this.handleUpdateView.bind(this);
        this.handleViewSelect = this.handleViewSelect.bind(this);
        this.toggleSection = this.toggleSection.bind(this);
        this.handleNewSetNameChange = this.handleNewSetNameChange.bind(this);
        this.handleSaveModeChange = this.handleSaveModeChange.bind(this);
    }

    /**
     * Genera una clave única para un grupo combinando el segmento y el ID del grupo.
     * Esto evita conflictos cuando hay grupos con el mismo nombre en diferentes secciones.
     * @param {string} fieldSegment - El segmento del campo ('DIMENSIONS', 'INDICATORS', 'METRICS')
     * @param {string} groupId - El ID del grupo (clave en FIELDS_GROUP_DATA)
     * @returns {string} - Clave única para el grupo
     */
    getGroupKey(fieldSegment, groupId) {
        return `${fieldSegment}_${groupId}`;
    }

    /**
     * Inicializa el estado de colapsado de las secciones según la configuración.
     * Los grupos definidos en DEFAULT_COLLAPSED_GROUPS aparecerán colapsados por defecto.
     * Todos los demás grupos aparecerán expandidos por defecto.
     * 
     * Nota: Cada grupo se identifica de forma única combinando el segmento y el ID del grupo
     * para evitar conflictos cuando hay grupos con el mismo nombre en diferentes secciones.
     */
    initializeCollapsedSections() {
        if (!this.props.fieldsData) return {};
        
        const { fieldsData } = this.props;
        const { FIELDS_GROUP_DATA } = fieldsData;
        const collapsedSections = {};
        
        // Inicializar todos los grupos posibles para cada segmento
        const segments = ['DIMENSIONS', 'INDICATORS', 'METRICS'];
        
        segments.forEach(segment => {
            Object.entries(FIELDS_GROUP_DATA).forEach(([groupId, groupData]) => {
                const groupKey = this.getGroupKey(segment, groupId);
                // Si el grupo está en DEFAULT_COLLAPSED_GROUPS, iniciarlo como colapsado (true)
                // De lo contrario, iniciarlo como expandido (false)
                collapsedSections[groupKey] = DEFAULT_COLLAPSED_GROUPS.includes(groupId);
            });
        });
        
        return collapsedSections;
    }

    componentDidMount() {
        // Inicializar el estado de colapsado de las secciones
        if (this.props.fieldsData) {
            const collapsedSections = this.initializeCollapsedSections();
            this.setState({ collapsedSections });
        }
    }

    componentDidUpdate(prevProps) {
        // Cuando el modal se abre, actualizar el estado con las props actuales
        if (!prevProps.isOpen && this.props.isOpen) {
            const activeFields = this.buildActiveFieldsFromProps(this.props.dimensions, this.props.metrics);
            
            // Verificar si las columnas actuales coinciden con el editingFieldsSet
            let activeView = null;
            let activeViewName = null;
            let saveMode = 'create';
            
            if (this.props.editingFieldsSet) {
                // Solo establecer como activo si las columnas coinciden exactamente
                if (this.fieldsMatchSet(this.props.editingFieldsSet, activeFields)) {
                    activeView = this.props.editingFieldsSet.id;
                    activeViewName = this.props.editingFieldsSet.name;
                    saveMode = 'update';
                }
                // Si no coinciden, activeView queda en null (mostrará "Set sin guardar")
            }
            
            // Reinicializar el estado de colapsado cuando se abre el modal
            const collapsedSections = this.initializeCollapsedSections();
            
            this.setState({
                activeFields: activeFields,
                originalFields: [...activeFields],
                activeView: activeView,
                activeViewName: activeViewName,
                hasUnsavedChanges: false,
                saveMode: saveMode,
                collapsedSections: collapsedSections,
            });
        }
        else if (prevProps.dimensions !== this.props.dimensions || prevProps.metrics !== this.props.metrics) {
            const activeFields = this.buildActiveFieldsFromProps(this.props.dimensions, this.props.metrics);
            this.setState({
                activeFields: activeFields,
                originalFields: [...activeFields],
            });
        }
        if (prevProps.editingFieldsSet !== this.props.editingFieldsSet) {
            if (this.props.editingFieldsSet) {
                this.setState({ 
                    activeView: this.props.editingFieldsSet.id,
                    activeViewName: this.props.editingFieldsSet.name,
                    saveMode: 'update'
                });
            } else {
                this.setState({ 
                    activeView: null,
                    activeViewName: null,
                    saveMode: 'create'
                });
            }
        }
    }

    getAllSectionNames() {
        const { fieldsData } = this.props;
        if (!fieldsData || !fieldsData.FIELDS_GROUP_DATA) return [];
        return Object.values(fieldsData.FIELDS_GROUP_DATA).map(group => group.name);
    }

    /**
     * Construye el array unificado de activeFields a partir de dimensions y metrics
     * Separa correctamente los indicators de los metrics si fieldsData está disponible
     * @param {Array<string>} dimensions - Array de dimension IDs
     * @param {Array<string>} metrics - Array de metric IDs (puede incluir indicators mezclados)
     * @returns {Array<string>} - Array unificado de field IDs en orden: dimensions, indicators, metrics
     */
    buildActiveFieldsFromProps(dimensions, metrics) {
        const { fieldsData } = this.props;
        const currentDimensions = dimensions || [];
        const currentMetrics = metrics || [];
        
        // Separar indicators de metrics si fieldsData está disponible
        let indicators = [];
        let pureMetrics = currentMetrics;
        if (fieldsData && fieldsData.FIELD_DATA_BY_ID && fieldsData.FIELD_SEGMENTS) {
            const separated = separateFieldsByType(currentMetrics, fieldsData);
            indicators = separated.indicators || [];
            pureMetrics = separated.metrics || [];
        }
        
        return combineFields(currentDimensions, pureMetrics, indicators);
    }

    handleSearchChange(event) {
        this.setState({ searchQuery: event.target.value });
    }

    handleFieldChange(event) {
        const { name, checked } = event.target;
        const [, fieldId] = name.split('#');

        this.setState(prevState => {
            let newFields;
            if (checked) {
                newFields = [...prevState.activeFields, fieldId];
            } else {
                newFields = prevState.activeFields.filter(id => id !== fieldId);
            }

            const hasChanges = !this.arraysEqual(newFields, prevState.originalFields);
            
            return {
                activeFields: newFields,
                hasUnsavedChanges: hasChanges && prevState.activeView !== null,
            };
        });
    }

    handleFieldsOrderChange(newOrder, fieldType) {
        // fieldType: 'DIMENSION', 'INDICATOR' o 'METRIC'
        this.setState(prevState => {
            const { fieldsData } = this.props;
            if (!fieldsData) return prevState;

            // Separar fields actuales por tipo
            const { dimensions, indicators, metrics } = separateFieldsByType(prevState.activeFields, fieldsData);
            
            // Actualizar el array correspondiente
            const updatedDimensions = fieldType === 'DIMENSION' ? newOrder : dimensions;
            const updatedIndicators = fieldType === 'INDICATOR' ? newOrder : indicators;
            const updatedMetrics = fieldType === 'METRIC' ? newOrder : metrics;
            
            // Combinar de nuevo manteniendo el orden: dimensions primero, luego indicators, luego metrics
            const newFields = combineFields(updatedDimensions, updatedMetrics, updatedIndicators);
            
            const hasChanges = !this.arraysEqual(newFields, prevState.originalFields);
            
            return {
                activeFields: newFields,
                hasUnsavedChanges: hasChanges && prevState.activeView !== null,
            };
        });
    }

    handleColumnRemove(columnId) {
        this.setState(prevState => {
            const newFields = prevState.activeFields.filter(id => id !== columnId);
            const hasChanges = !this.arraysEqual(newFields, prevState.originalFields);
            
            return {
                activeFields: newFields,
                hasUnsavedChanges: hasChanges && prevState.activeView !== null,
            };
        });
    }

    arraysEqual(arr1, arr2) {
        if (arr1.length !== arr2.length) return false;
        return arr1.every((val, index) => val === arr2[index]);
    }

    /**
     * Compara si las columnas actuales coinciden exactamente con un set de columnas
     * @param {Object} fieldsSet - El set de columnas a comparar
     * @param {Array} currentFields - Array unificado de field IDs actualmente seleccionados
     * @returns {boolean} - true si coinciden exactamente en contenido y orden
     */
    fieldsMatchSet(fieldsSet, currentFields) {
        if (!fieldsSet || !fieldsSet.fields || fieldsSet.fields.length === 0) {
            return false;
        }

        const { fieldsData } = this.props;
        if (!fieldsData) return false;

        // Comparar arrays directamente: deben tener la misma longitud y los mismos elementos en el mismo orden
        return this.arraysEqual(currentFields, fieldsSet.fields);
    }

    handleViewSelect(event) {
        const selectedViewId = event.target.value;
        if (selectedViewId === "" || selectedViewId === "custom") {
            // Vista personalizada
            this.setState({
                activeView: null,
                activeViewName: null,
                hasUnsavedChanges: false,
                originalFields: [...this.state.activeFields],
            });
        } else {
            // Cargar vista guardada
            const selectedSet = this.props.fieldsSets.find(set => set.id.toString() === selectedViewId);
            if (selectedSet && selectedSet.fields) {
                // Usar directamente el array de fields del set
                const fields = [...selectedSet.fields];
                
                this.setState({
                    activeView: selectedSet.id,
                    activeViewName: selectedSet.name,
                    activeFields: fields,
                    originalFields: fields,
                    hasUnsavedChanges: false,
                });
            }
        }
    }

    handleApply() {
        // Separar fields en dimensions, indicators y metrics
        const { fieldsData } = this.props;
        const { dimensions, indicators, metrics } = separateFieldsByType(this.state.activeFields, fieldsData);
        
        // Incluir indicators en metrics para mantener compatibilidad con el componente padre
        // que solo espera dimensions y metrics
        const combinedMetrics = [...indicators, ...metrics];
        
        // Pasar el ID del set activo si existe para que el componente padre pueda establecer editingFieldsSet correctamente
        const activeViewId = this.state.activeView;
        this.props.onApply(dimensions, combinedMetrics, activeViewId);
        this.props.toggle();
    }

    handleSaveModeChange(mode) {
        this.setState({ saveMode: mode, savingError: null });
    }

    handleNewSetNameChange(event) {
        this.setState({ newSetName: event.target.value, savingError: null });
    }

    handleSaveAsNew() {
        this.setState({ showSaveAsInput: true, saveMode: 'create' });
    }

    handleCancelSave() {
        this.setState({ 
            showSaveAsInput: false, 
            newSetName: "",
            savingError: null
        });
    }

    handleUpdateView() {
        if (!this.state.activeView) return;
        
        // Obtener el nombre del set desde fieldsSets para asegurar que sea el nombre correcto
        // sin asteriscos u otros modificadores visuales
        let fieldsSetName = this.state.activeViewName || '';
        
        // Si el nombre tiene un asterisco (indica cambios sin guardar), removerlo
        fieldsSetName = fieldsSetName.replace(/\s*\*.*$/, '').trim();
        
        // Si aún no tenemos nombre, buscar en fieldsSets usando el activeView ID
        if (!fieldsSetName && this.props.fieldsSets) {
            const currentSet = this.props.fieldsSets.find(set => set.id === this.state.activeView);
            if (currentSet) {
                fieldsSetName = currentSet.name;
            }
        }
        
        this.handleSaveFieldsSet(null, fieldsSetName, 'update');
    }

    handleSaveFieldsSet(event, setNameOverride = null, modeOverride = null) {
        if (event) {
            event.preventDefault();
        }
        const { t, sourceType, editingFieldsSet } = this.props;
        const fieldsSetName = setNameOverride || this.state.newSetName;
        const saveMode = modeOverride || this.state.saveMode;

        if (!fieldsSetName || fieldsSetName.trim() === '') {
            this.setState({ savingError: t('data_explorer.fields_set_name_required', 'El nombre del set es requerido') });
            return;
        }

        if (this.state.activeFields.length === 0) {
            this.setState({ savingError: t('data_explorer.select_at_least_one_field', 'Debe seleccionar al menos un columnas') });
            return;
        }

        const allFields = [...this.state.activeFields];

        this.setState({ isSaving: true, savingError: null });

        const fieldsSetData = {
            name: fieldsSetName.trim(),
            fields: allFields,
            is_shared: false,
            only_available_at: 'DATA_EXPLORER',
            data_source_type: sourceType,
        };

        // En modo update, siempre incluir el ID del set que se está actualizando
        if (saveMode === 'update') {
            // Priorizar activeView (más confiable cuando el usuario selecciona un set),
            // luego editingFieldsSet.id, luego buscar en fieldsSets por nombre
            let setIdToUpdate = null;
            if (this.state.activeView) {
                // activeView es el más confiable porque se establece cuando el usuario selecciona un set
                setIdToUpdate = this.state.activeView;
            } else if (editingFieldsSet && editingFieldsSet.id) {
                setIdToUpdate = editingFieldsSet.id;
            } else if (this.props.fieldsSets && fieldsSetName) {
                // Buscar el set por nombre como último recurso (menos confiable)
                const foundSet = this.props.fieldsSets.find(set => set.name === fieldsSetName.trim());
                if (foundSet) {
                    setIdToUpdate = foundSet.id;
                }
            }
            
            if (setIdToUpdate) {
                // Asegurar que el ID sea un número (el backend puede esperar un número)
                fieldsSetData.id = typeof setIdToUpdate === 'string' ? parseInt(setIdToUpdate, 10) : setIdToUpdate;
                
                // Debug: Log para verificar que el ID se está pasando correctamente
                console.log('Actualizando set:', {
                    setId: fieldsSetData.id,
                    setName: fieldsSetData.name,
                    activeView: this.state.activeView,
                    editingFieldsSet: editingFieldsSet?.id
                });
            } else {
                // Si no se puede determinar el ID, no es una actualización válida
                console.error('Error: No se pudo determinar el ID del set a actualizar', {
                    activeView: this.state.activeView,
                    editingFieldsSet: editingFieldsSet,
                    fieldsSetName: fieldsSetName,
                    fieldsSets: this.props.fieldsSets
                });
                this.setState({ 
                    isSaving: false,
                    savingError: t('data_explorer.error_updating_set', 'Error: No se pudo determinar el set a actualizar')
                });
                return;
            }
        }

        // Debug: verificar que el ID se esté pasando correctamente
        if (saveMode === 'update' && !fieldsSetData.id) {
            console.error('Error: Intentando actualizar un set sin ID', {
                editingFieldsSet,
                activeView: this.state.activeView,
                fieldsSetName,
                fieldsSetData
            });
            this.setState({ 
                isSaving: false,
                savingError: t('data_explorer.error_updating_set', 'Error: No se pudo determinar el set a actualizar')
            });
            return;
        }

        FieldsColumnsSettingsService.saveFieldsSet(fieldsSetData)
            .then((savedSet) => {
                this.setState({ 
                    isSaving: false, 
                    savingError: null,
                    showSaveAsInput: false,
                    newSetName: "",
                });
                
                if (saveMode === 'create') {
                    this.setState({ 
                        activeView: savedSet.id,
                        activeViewName: savedSet.name,
                        originalFields: [...this.state.activeFields],
                        hasUnsavedChanges: false,
                    });
                } else {
                    // En modo update, mantener el activeView con el ID del set guardado
                    const updatedActiveView = savedSet.id || this.state.activeView;
                    this.setState({
                        activeView: updatedActiveView,
                        activeViewName: savedSet.name,
                        originalFields: [...this.state.activeFields],
                        hasUnsavedChanges: false,
                    });
                }
                
                if (this.props.onFieldsSetSaved) {
                    this.props.onFieldsSetSaved(savedSet);
                }
                
                const successMessage = saveMode === 'create' 
                    ? t('data_explorer.fields_set_created_successfully', 'Set de columnas creado exitosamente')
                    : t('data_explorer.fields_set_updated_successfully', 'Set de columnas actualizado exitosamente');
                alert(successMessage);
            })
            .catch((error) => {
                this.setState({ isSaving: false });
                let errorMessage = t('data_explorer.error_saving_fields_set', 'Error al guardar el set de columnas');
                
                if (error && error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
                    const firstError = error.errors[0];
                    if (firstError.code === 'DUPLICATE_PRIVATE_NAME') {
                        errorMessage = t('data_explorer.duplicate_private_name_error', 'Ya existe un set privado con este nombre. Por favor, elige otro nombre o actualiza el set existente.');
                    } else if (firstError.code === 'DUPLICATE_SHARED_NAME') {
                        errorMessage = t('data_explorer.duplicate_shared_name_error', 'Ya existe un set compartido con este nombre. Por favor, elige otro nombre.');
                    } else if (firstError.code === 'DUPLICATE_NAME_WITH_SHARED') {
                        errorMessage = t('data_explorer.duplicate_name_with_shared_error', 'Ya existe un set compartido con este nombre. Los sets privados no pueden tener el mismo nombre que los compartidos.');
                    } else if (firstError.message) {
                        errorMessage = firstError.message;
                    }
                } else if (error && error.message) {
                    errorMessage = error.message;
                }
                
                this.setState({ savingError: errorMessage });
            });
    }

    /**
     * Alterna el estado de colapsado de una sección.
     * @param {string} fieldSegment - El segmento del campo ('DIMENSIONS', 'INDICATORS', 'METRICS')
     * @param {string} groupId - El ID del grupo (clave en FIELDS_GROUP_DATA)
     */
    toggleSection(fieldSegment, groupId) {
        const groupKey = this.getGroupKey(fieldSegment, groupId);
        this.setState(prevState => ({
            collapsedSections: {
                ...prevState.collapsedSections,
                [groupKey]: !prevState.collapsedSections[groupKey]
            }
        }));
    }

    filterFieldsBySearch(fields) {
        if (!this.state.searchQuery.trim()) return fields;

        const query = this.state.searchQuery.toLowerCase();
        return fields.filter(field => {
            const name = field.name || '';
            return name.toLowerCase().includes(query);
        });
    }

    renderFieldChoice(fieldType, field) {
        const isChecked = this.state.activeFields.includes(field.id);

        return (
            <div key={field.id} className="form-item -inline">
                <Switch checked={isChecked} name={`${fieldType}#${field.id}`} onChange={this.handleFieldChange} />
                <div className="combined-label">
                    <p className="combined-label-title">
                        {field.name}
                        <FieldHelpIcon fieldId={field.id} />
                    </p>
                </div>
            </div>
        );
    }
    
    renderFieldsSection(title, fieldSegment) {
        const { fieldsData, sourceType } = this.props;
        if (!fieldsData) return null;

        const { FIELDS_BY_DATA_SOURCE_TYPE, FIELD_DATA_BY_ID, FIELD_SEGMENTS, FIELDS_GROUP_DATA } = fieldsData;
        const availableFieldIds = FIELDS_BY_DATA_SOURCE_TYPE[sourceType];
        if (!availableFieldIds) return null;

        const fieldsByGroup = {};
        Object.keys(FIELDS_GROUP_DATA).forEach(groupId => fieldsByGroup[groupId] = []);

        availableFieldIds.forEach(id => {
            const field = FIELD_DATA_BY_ID[id] ? { ...FIELD_DATA_BY_ID[id], id } : null;
            if (field && field.segment === FIELD_SEGMENTS[fieldSegment] && field.group) {
                if (fieldsByGroup[field.group]) {
                    fieldsByGroup[field.group].push(field);
                }
            }
        });

        // Verificar si hay al menos un grupo con campos disponibles
        const hasAnyFields = Object.values(fieldsByGroup).some(groupFields => {
            if (!groupFields || groupFields.length === 0) return false;
            const filteredFields = this.filterFieldsBySearch(groupFields);
            // Si hay búsqueda activa, considerar solo los campos filtrados
            // Si no hay búsqueda, considerar todos los campos del grupo
            return this.state.searchQuery.trim() 
                ? filteredFields.length > 0 
                : true;
        });

        // Si no hay campos disponibles para este segmento, no renderizar la sección
        if (!hasAnyFields) return null;

        return (
            <div className="pt-0">
                <div className="form-header">
                    <h3 className="form-title">{title}</h3>
                </div>
                {Object.entries(FIELDS_GROUP_DATA).map(([groupId, groupData]) => {
                    const groupFields = fieldsByGroup[groupId];
                    if (!groupFields || groupFields.length === 0) return null;
                    
                    const filteredFields = this.filterFieldsBySearch(groupFields);
                    if (filteredFields.length === 0 && this.state.searchQuery.trim()) return null;
                    
                    // Generar clave única combinando el segmento y el ID del grupo
                    const groupKey = this.getGroupKey(fieldSegment, groupId);
                    // Obtener el estado de colapsado. Si no está definido, usar false (expandido) por defecto
                    // Los grupos en DEFAULT_COLLAPSED_GROUPS se inicializan como true (colapsado)
                    const isCollapsed = this.state.collapsedSections[groupKey] === true;

                    return (
                        <div key={groupId} className="column-section-accordion">
                            <div
                                className="accordion-header"
                                onClick={() => this.toggleSection(fieldSegment, groupId)}
                            >
                                <h4 className="form-subtitle">{groupData.name}</h4>
                                <FaIcon
                                    iconName="chevron-right"
                                    className={isCollapsed ? "accordion-chevron collapsed" : "accordion-chevron expanded"}
                                />
                            </div>
                            {!isCollapsed && (
                                <div className="form-grid row-col-2">
                                    {filteredFields.map(field => this.renderFieldChoice(field.type, field))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
    
    getFieldDataForActiveList(fieldId) {
        const { fieldsData, t } = this.props;
        const fieldData = fieldsData && fieldsData.FIELD_DATA_BY_ID[fieldId];
        const title = fieldData ? fieldData.name : fieldId;
        const descriptionKey = getFieldDescriptionKey(fieldId);
        
        return { 
            title,
            descriptionKey: descriptionKey ? t(descriptionKey) : null,
            hasDescription: hasFieldDescription(fieldId)
        };
    }

    render() {
        const { t, isOpen, toggle, fieldsSets, fieldsData } = this.props;
        const totalSelected = this.state.activeFields.length;
        const activeViewId = this.state.activeView ? this.state.activeView.toString() : "custom";
        
        // Separar fields por tipo para renderizado diferenciado
        const { dimensions, indicators, metrics } = separateFieldsByType(this.state.activeFields, fieldsData);

        const viewSets = (fieldsSets || []).map(set => {
            const displayName = this.state.activeView === set.id && this.state.hasUnsavedChanges 
                ? `${set.name} *${set.is_shared ? ` (${t('fields_sets_list.shared', 'Compartido')})` : ''}` 
                : `${set.name}${set.is_shared ? ` (${t('fields_sets_list.shared', 'Compartido')})` : ''}`;
            return {
                id: set.id,
                name: set.name,
                displayName
            };
        });

        return (
            <Modal 
                isOpen={isOpen} 
                toggle={toggle} 
                size="lg" 
                className="columns-selector-modal data-explorer-columns-modal"
            >
                <ModalHeader toggle={toggle} className="columns-selector-modal__header">
                    <FaIcon iconName="columns"/>{t('data_explorer.configure_columns')}
                </ModalHeader>
                <ModalBody className="columns-selector-modal__body data-explorer-body">
                    {renderSearchBar({
                        searchQuery: this.state.searchQuery,
                        placeholder: t('data_explorer.search_placeholder', 'Buscar dimensiones o métricas...'),
                        onSearchChange: this.handleSearchChange
                    })}

                    <div className="columns-selector-modal__panels-container d-flex">
                        {/* Panel Izquierdo: Columnas Disponibles */}
                        <div className="columns-selector-modal__left-panel">
                            <h3 className="columns-selector-modal__panel-title">
                                {t('data_explorer.available_columns', 'Columnas Disponibles')}
                            </h3>
                            {this.renderFieldsSection(t('data_explorer.dimensions'), 'DIMENSIONS')}
                            {this.renderFieldsSection(t('data_explorer.indicators'), 'INDICATORS')}
                            {this.renderFieldsSection(t('data_explorer.metrics'), 'METRICS')}
                        </div>
                        
                        {/* Panel Derecho: Columnas Seleccionadas */}
                        <div className="columns-selector-modal__right-panel p-4 pt-0">
                            <h3 className="columns-selector-modal__panel-title">
                                {t('data_explorer.selected_columns', 'Columnas Seleccionadas')} ({totalSelected})
                            </h3>
                            {totalSelected === 0 ? (
                                <div className="columns-selector-modal__empty-message">
                                    <p>{t('data_explorer.select_columns_message', 'Selecciona columnas del panel izquierdo para empezar a construir tu vista.')}</p>
                                </div>
                            ) : (
                                <>
                                    {dimensions.length > 0 && (
                                        <>
                                            <div className="form-header">
                                                <h3 className="form-title">{t('data_explorer.dimensions')}</h3>
                                            </div>
                                            <ActiveColumnsList
                                                activeColumns={dimensions}
                                                onColumnsPositionChange={(newOrder) => this.handleFieldsOrderChange(newOrder, 'DIMENSION')}
                                                getColumnData={(id) => this.getFieldDataForActiveList(id)}
                                                onColumnRemove={this.handleColumnRemove}
                                            />
                                        </>
                                    )}
                                    {indicators.length > 0 && (
                                        <>
                                            <div className="form-header">
                                                <h3 className="form-title">{t('data_explorer.indicators')}</h3>
                                            </div>
                                            <ActiveColumnsList
                                                activeColumns={indicators}
                                                onColumnsPositionChange={(newOrder) => this.handleFieldsOrderChange(newOrder, 'INDICATOR')}
                                                getColumnData={(id) => this.getFieldDataForActiveList(id)}
                                                onColumnRemove={this.handleColumnRemove}
                                            />
                                        </>
                                    )}
                                    {metrics.length > 0 && (
                                        <>
                                            <div className="form-header">
                                                <h3 className="form-title">{t('data_explorer.metrics')}</h3>
                                            </div>
                                            <ActiveColumnsList
                                                activeColumns={metrics}
                                                onColumnsPositionChange={(newOrder) => this.handleFieldsOrderChange(newOrder, 'METRIC')}
                                                getColumnData={(id) => this.getFieldDataForActiveList(id)}
                                                onColumnRemove={this.handleColumnRemove}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter className="columns-selector-modal__footer">
                    {renderViewSelector({
                        activeViewId,
                        viewSets,
                        viewSelectorLabel: t('data_explorer.current_view', 'Set de columnas'),
                        customViewLabel: t('data_explorer.custom_view', 'Set sin guardar'),
                        saveAsNewLabel: t('data_explorer.save_as_new', 'Guardar Nuevo'),
                        saveViewLabel: t('data_explorer.save_view', 'Guardar'),
                        updateViewLabel: t('data_explorer.update_view', 'Actualizar Set'),
                        hasActiveView: !!this.state.activeView,
                        hasUnsavedChanges: this.state.hasUnsavedChanges,
                        showSaveAsInput: this.state.showSaveAsInput,
                        newSetName: this.state.newSetName,
                        isSaving: this.state.isSaving,
                        setNamePlaceholder: t('data_explorer.fields_set_name_placeholder', 'Nombre del set'),
                        onViewSelect: this.handleViewSelect,
                        onSaveAsNew: this.handleSaveAsNew,
                        onSaveView: (e) => this.handleSaveFieldsSet(e),
                        onUpdateView: this.handleUpdateView,
                        onNameChange: this.handleNewSetNameChange,
                        onCancel: this.handleCancelSave,
                        t
                    })}

                    {renderActionButtons({
                        hasActiveView: !!this.state.activeView,
                        hasUnsavedChanges: this.state.hasUnsavedChanges,
                        showSaveAsInput: this.state.showSaveAsInput,
                        newSetName: this.state.newSetName,
                        isSaving: this.state.isSaving,
                        saveViewLabel: t('data_explorer.save_view', 'Guardar'),
                        updateViewLabel: t('data_explorer.update_view', 'Actualizar Set'),
                        setNamePlaceholder: t('data_explorer.fields_set_name_placeholder', 'Nombre del set'),
                        onSaveAsNew: this.handleSaveAsNew,
                        onSaveView: (e) => this.handleSaveFieldsSet(e),
                        onUpdateView: this.handleUpdateView,
                        onApply: this.handleApply,
                        onClose: toggle,
                        onNameChange: this.handleNewSetNameChange,
                        t
                    })}

                    {renderError(this.state.savingError)}
                </ModalFooter>
            </Modal>
        );
    }
}

export const DataExplorerColumnsModal = withTranslation()(DataExplorerColumnsModalComponent);

